import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import AnimateHeight from '../../../../../components/AnimateHeight'
import DefaultModal from '../../../../../components/DefaultModal'
import FormButton from '../../../../../components/FormButton'
import FormInput from '../../../../../components/FormInput'
import MessageBox from '../../../../../components/MessageBox'
import { useSetVeliOrganizationVatNumberMutation } from '../../../../../redux/services/coreApi'

type FormData = {
  vatNumber: string
}

const schema = yup.object({
  vatNumber: yup.string().required('This field is required.'),
})

// Blocking prompt for a converted Veli corporate whose company account has no VAT number yet.
//
// The Veli export carried no company columns at all, so every corporate client was created as an
// individual. Correcting those accounts could not wait on collecting VAT numbers we do not hold and
// cannot look up, so the conversion ran first and left the field empty — which is why
// Organization.VatNumber is nullable at all, rather than carrying a placeholder into a field that
// feeds KYB and regulatory reporting.
//
// Deliberately blocking (no close button, overlay click inert): the VAT is required for the company
// verification that comes next, so there is nothing useful the customer can do until it is supplied.
// The server stores it on the organization and clears the flag itself, so this never reappears.
const VeliOrganizationVatModal: React.FC = () => {
  const [error, setError] = useState<string | null>(null)
  const [setVatNumber, { isLoading }] =
    useSetVeliOrganizationVatNumberMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const onSubmit: SubmitHandler<FormData> = async ({ vatNumber }) => {
    setError(null)
    try {
      // Invalidates the User cache, so the flag refetches and this modal unmounts on its own.
      await setVatNumber({ vatNumber }).unwrap()
    } catch {
      setError("We couldn't save that just now. Please try again.")
    }
  }

  return (
    <DefaultModal isOpen onClose={() => {}} isLoading={isLoading} inlineLoading>
      <div className="p-5 space-y-4">
        <div>
          <h2 className="font-semi text-lg mb-2">
            We need your company's VAT number
          </h2>
          <p className="text-description text-sm">
            We've moved your account over as a company account. Your Veli record
            didn't include a VAT number, so we need it before we can verify your
            company.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <FormInput
            label="VAT number"
            inputMode="text"
            placeholder="SI12345678"
            registeredForm={register('vatNumber')}
            error={errors?.vatNumber}
          />
          <p className="text-description text-xs">
            Include the country prefix. We'll verify it along with your company
            documents when you complete your identity check.
          </p>

          <AnimateHeight isVisible={error !== null}>
            <MessageBox title={error ?? ''} type="warning" />
          </AnimateHeight>

          <FormButton title="Save and continue" isLoading={isLoading} />
        </form>
      </div>
    </DefaultModal>
  )
}

export default VeliOrganizationVatModal
